/**
 * LoginPage — Connexion par email + mot de passe.
 *
 * Refonte R5 (mai 2026) :
 *   - LogoMark ShieldCheck tech au-dessus du titre
 *   - Card glass + Field/Input/Alert R2
 *   - Alert avec icône CheckCircle2 si justReset
 *   - Renvoi mail de vérification inline si EMAIL_NOT_VERIFIED
 *
 * Logique métier 100% préservée : login + redirect dashboard, gestion erreurs API,
 * EMAIL_NOT_VERIFIED → bouton renvoi, justReset query param.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button, Input, Field, Card, Alert, LogoMark, SectionEyebrow } from '@/components/ui';
import { VpcMairieIcon } from '@/components/ui/VpcMairieIcon';
import { PublicLayout } from '@/components/PublicLayout';
import { useAuth } from '@/lib/auth-context';
import { api, ApiException } from '@/lib/api';
import { CheckCircle2, AlertCircle } from 'lucide-react';

export function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const justReset = params.get('reset') === '1';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Pour le cas EMAIL_NOT_VERIFIED : permettre un renvoi du mail rapide
  const [resendLoading, setResendLoading] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setErrorCode(null);
    setResendDone(false);
    setLoading(true);
    try {
      await login({ email, password });
      // On repart là où l'utilisateur voulait aller — typiquement le QR d'une
      // armoire scanné hors session. À défaut, le tableau de bord.
      let cible = '/dashboard';
      try {
        const memo = window.sessionStorage.getItem('vpc:apres-connexion');
        window.sessionStorage.removeItem('vpc:apres-connexion');
        // Chemin interne UNIQUEMENT : `//` ouvrirait un domaine externe, ce
        // serait une redirection ouverte.
        if (memo && memo.startsWith('/') && !memo.startsWith('//')) cible = memo;
      } catch { /* navigation privée */ }
      navigate(cible);
    } catch (err) {
      if (err instanceof ApiException) {
        setErrorCode(err.payload.code ?? null);
        setError(err.payload.error);
      } else {
        setError('Une erreur est survenue, veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!email) return;
    setResendLoading(true);
    try {
      await api.auth.resendVerification({ email });
      setResendDone(true);
    } catch {
      // Erreur silencieuse, le user reverra l'erreur dans le bandeau si pertinent
    } finally {
      setResendLoading(false);
    }
  }

  return (
    <PublicLayout maxWidth="sm">
      <div className="space-y-6">
        {/* En-tête : LogoMark + eyebrow + titre */}
        <div className="text-center">
          <div className="flex justify-center">
            <LogoMark icon={VpcMairieIcon} accent="tech" size="lg" aria-label="Vision Pro Collectivité" />
          </div>
          <SectionEyebrow as="div" className="mt-4">Vision Pro Collectivité</SectionEyebrow>
          <h1 className="mt-1 font-display text-xl font-semibold tracking-r-tight text-ink sm:text-2xl">
            Connexion
          </h1>
          <p className="mt-2 text-sm text-ink-muted">
            Connectez-vous à votre espace administré, agent ou élu.
          </p>
          <p className="mt-0.5 text-xs text-ink-subtle">
            Une solution One For All Distribution
          </p>
        </div>

        {justReset && (
          <Alert variant="success" icon={CheckCircle2}>
            Votre mot de passe a été modifié. Vous pouvez maintenant vous connecter.
          </Alert>
        )}

        <Card variant="glass" padded>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <Alert variant="error" icon={AlertCircle}>
                {error}
                {errorCode === 'EMAIL_NOT_VERIFIED' && (
                  <>
                    {' '}
                    {resendDone ? (
                      <span className="font-medium">
                        Si l'adresse est connue, un nouveau lien vient d'être envoyé.
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={handleResend}
                        disabled={resendLoading || !email}
                        className="font-medium underline hover:no-underline disabled:opacity-50"
                      >
                        {resendLoading ? 'Envoi en cours…' : 'Renvoyer le mail de vérification'}
                      </button>
                    )}
                  </>
                )}
              </Alert>
            )}

            <Field label="Adresse email" htmlFor="email" required>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="prenom.nom@mairie.fr"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
                className="h-12 sm:h-11"
              />
            </Field>

            <Field
              label="Mot de passe"
              htmlFor="password"
              required
            >
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-12 sm:h-11"
              />
              <div className="mt-1.5 text-right">
                <Link
                  to="/mot-de-passe-oublie"
                  className="text-xs text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md"
                >
                  Mot de passe oublié&nbsp;?
                </Link>
              </div>
            </Field>

            <Button type="submit" loading={loading} className="w-full" size="lg">
              Se connecter
            </Button>
          </form>
        </Card>

        <p className="text-center text-sm text-ink-muted">
          Pas encore de compte&nbsp;?{' '}
          <Link
            to="/inscription"
            className="font-medium text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg rounded-r-md"
          >
            Créer un compte administré
          </Link>
        </p>
      </div>
    </PublicLayout>
  );
}
